import { Logger } from './Logger';

export class AudioSystem {
  private context?: AudioContext;
  private musicGain?: GainNode;
  private musicNodes: OscillatorNode[] = [];

  public constructor(
    private muted: boolean,
    private readonly logger: Logger,
  ) {}

  public get isMuted(): boolean {
    return this.muted;
  }

  public async unlock(): Promise<void> {
    try {
      this.context ??= new AudioContext();
      if (this.context.state === 'suspended') await this.context.resume();
    } catch (error) {
      this.logger.warn('Web Audio is unavailable.', error);
    }
  }

  public setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.musicGain && this.context) {
      this.musicGain.gain.setTargetAtTime(muted ? 0.0001 : 0.012, this.context.currentTime, 0.08);
    }
  }

  public startMusic(): void {
    if (!this.context || this.musicNodes.length > 0) return;
    this.musicGain = this.context.createGain();
    this.musicGain.gain.setValueAtTime(this.muted ? 0.0001 : 0.012, this.context.currentTime);
    this.musicGain.connect(this.context.destination);
    this.musicNodes = [
      this.createDrone(55, 'triangle'),
      this.createDrone(82.5, 'sine'),
      this.createDrone(110, 'square', 0.16),
    ];
  }

  public setPaused(paused: boolean): void {
    if (!this.musicGain || !this.context) return;
    const volume = paused || this.muted ? 0.0001 : 0.012;
    this.musicGain.gain.setTargetAtTime(volume, this.context.currentTime, 0.1);
  }

  public stopMusic(): void {
    this.musicNodes.forEach((node) => node.stop());
    this.musicNodes = [];
    this.musicGain?.disconnect();
    this.musicGain = undefined;
  }

  public play(
    kind:
      | 'confirm'
      | 'laser'
      | 'empty'
      | 'lowAmmo'
      | 'ammoPickup'
      | 'lifePickup'
      | 'hit'
      | 'explosion'
      | 'gameover',
  ): void {
    if (this.muted || !this.context) return;
    const presets = {
      confirm: [520, 0.06, 0.025],
      laser: [760, 0.045, 0.018],
      empty: [95, 0.04, 0.012],
      lowAmmo: [280, 0.08, 0.014],
      ammoPickup: [620, 0.14, 0.026],
      lifePickup: [880, 0.22, 0.026],
      hit: [110, 0.12, 0.04],
      explosion: [70, 0.18, 0.055],
      gameover: [150, 0.5, 0.045],
    } as const;
    const [frequency, duration, volume] = presets[kind];
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = ['laser', 'empty', 'lowAmmo'].includes(kind) ? 'square' : 'sawtooth';
    oscillator.frequency.setValueAtTime(frequency, this.context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(
      Math.max(30, frequency * 0.45),
      this.context.currentTime + duration,
    );
    gain.gain.setValueAtTime(volume, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start();
    oscillator.stop(this.context.currentTime + duration);
  }

  public destroy(): void {
    this.stopMusic();
    void this.context?.close();
    this.context = undefined;
  }

  private createDrone(frequency: number, type: OscillatorType, detune = 0): OscillatorNode {
    const oscillator = this.context!.createOscillator();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    oscillator.detune.value = detune;
    oscillator.connect(this.musicGain!);
    oscillator.start();
    return oscillator;
  }
}
